import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, defaultDataIdFromObject } from "@apollo/client";
import type { FieldPolicy, InMemoryCacheConfig } from "@apollo/client/cache";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { ApolloProvider } from "@apollo/client/react";
import { relayStylePagination } from "@apollo/client/utilities";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { map } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { handleLoginErrorCode, validateAuth0ApiErrors } from "../../context/utilsAuth0";
import { isSessionExpiredError, requireToken } from "../lib/authErrors";
import { clearClientData } from "../lib/clearClientData";
import { useAccountContext } from "./AccountResolver";
import { isSessionExpired, useOverlayControl } from "./AppOverlayController";

const FETCH_TIMEOUT_MS = 62_000;

// Custom fetch with AbortController timeout
const sbFetch: typeof fetch = (input: string | URL | Request, init?: RequestInit): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout((): void => {
    controller.abort();
  }, FETCH_TIMEOUT_MS);
  return fetch(input, { ...init, signal: controller.signal }).finally((): void => {
    clearTimeout(timeoutId);
  });
};

interface Props {
  children: React.ReactNode;
}

export const GraphQLShell: React.FC<Props> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { reportConnectivityLost, reportSessionExpired, sessionExpired } = useOverlayControl();
  const { urlName } = useAccountContext();

  // Refs so the link closures always call the latest versions
  const navigateRef = useRef(navigate);
  const reportConnectivityLostRef = useRef(reportConnectivityLost);
  const reportSessionExpiredRef = useRef(reportSessionExpired);
  const urlNameRef = useRef(urlName);

  useLayoutEffect(() => {
    navigateRef.current = navigate;
    reportConnectivityLostRef.current = reportConnectivityLost;
    reportSessionExpiredRef.current = reportSessionExpired;
    urlNameRef.current = urlName;
  });

  // Track the previous urlName so we can detect account switches and clear the cache.
  // The old ApolloProvider recreated the entire client (giving a fresh InMemoryCache) whenever
  // appContext.urlName changed. We keep a single client here for connectivity recovery, but we
  // must clear the cache on account switch so stale data from the previous account is not served.
  const prevUrlNameRef = useRef<string | null | undefined>(undefined);

  const client: ApolloClient = useMemo((): ApolloClient => {
    // Error classifier link — uses v4 ErrorLink API with `error` (not graphQLErrors/networkError)
    // eslint-disable-next-line react-hooks/refs -- refs are accessed when the callback fires (outside render), not when the closure is created here
    const errorLink = new ErrorLink(({ error, operation }): void => {
      // Ignore in-flight request errors once the session is expired (app is tearing down).
      if (isSessionExpired()) {
        return;
      }

      const context = operation.getContext() as { headers?: Record<string, string> };
      const reqId = context.headers?.["Request-ID"] ?? "unknown";

      // Thrown by authLink when the token can't be acquired: warn + breadcrumb, no Sentry error and
      // no tokenless request. The heartbeat owns the connectivity overlay; this recovers on reconnect.
      if (error.name === "TokenUnavailableError") {
        console.warn(
          `[GraphQL] auth token unavailable — ${operation.operationName ?? "unknown"} (requestId: ${reqId})`
        );
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Auth token unavailable",
          level: "warning",
        });
        return;
      }

      if (CombinedGraphQLErrors.is(error)) {
        const extensionCodes = error.errors.map(
          (e) =>
            // eslint-disable-next-line @typescript-eslint/dot-notation -- extensions is typed as Record<string, unknown>
            (e.extensions as Record<string, unknown> | null | undefined)?.["code"] as string | undefined
        );

        // Handle error codes from standard GraphQL errors ({ data: null, errors: [...] }). Errors
        // nested inside the data are handled by forwardLink.
        const authCode = extensionCodes.find((c) => c !== undefined);
        if (
          authCode !== undefined &&
          // Invalid/expired token → session-expired overlay, not an abrupt full-page logout.
          handleLoginErrorCode(authCode, navigateRef.current)
        ) {
          return;
        }

        error.errors.forEach((gqlErr, i) => {
          const msg = typeof gqlErr.message === "string" ? gqlErr.message : "unknown error";
          const code = extensionCodes[i];
          const codeLabel = code !== undefined ? ` [${code}]` : "";
          console.error(
            `[GraphQL] ${operation.operationName ?? "unknown"}: ${msg}${codeLabel} (requestId: ${reqId})`,
            gqlErr
          );
          Sentry.captureException(new Error(`[GraphQL] ${operation.operationName ?? "unknown"}: ${msg}`), {
            tags: {
              operation: operation.operationName,
              requestId: reqId,
              ...(code !== undefined && { errorCode: code }),
            },
            extra: { graphqlError: gqlErr, path: gqlErr.path },
          });
        });
        return;
      }

      // Network / other errors
      const statusCode = "statusCode" in error && typeof error.statusCode === "number" ? error.statusCode : 0;

      // TypeError + no status = raw network failure (browser offline, DNS failure, CORS block)
      if (error.name === "AbortError" || statusCode >= 500 || (error instanceof TypeError && statusCode === 0)) {
        console.error(`[GraphQL] connectivity error — ${operation.operationName ?? "unknown"}`, error);
        reportConnectivityLostRef.current();
        Sentry.addBreadcrumb({
          category: "network",
          message: `Connectivity lost — ${error.message}`,
          level: "error",
        });
        return;
      }

      if (statusCode === 403) {
        toast.error(`You don't have permission for this operation.\nContact CS with ID ${reqId}`, {
          autoClose: false,
          style: { whiteSpace: "pre-line" },
        });
        void navigateRef.current("/dashboard");
        return;
      }

      Sentry.captureException(error, {
        tags: { operation: operation.operationName, requestId: reqId },
      });
    });

    // Auth link — uses v4 SetContextLink API: (prevContext, operation)
    const authLink = new SetContextLink(
      // eslint-disable-next-line react-hooks/refs -- refs are accessed when the callback fires (outside render), not when the closure is created here
      async (prevContext: Readonly<ApolloLink.OperationContext>): Promise<Partial<ApolloLink.OperationContext>> => {
        // eslint-disable-next-line @typescript-eslint/dot-notation -- OperationContext is Record<string, any>; dot notation unavailable
        const headers = prevContext["headers"] as Record<string, string> | undefined;
        // eslint-disable-next-line @typescript-eslint/dot-notation -- OperationContext is Record<string, any>; dot notation unavailable
        const skipAccountUri = prevContext["skipAccountUri"] === true;
        const reqId = uuidv4();
        try {
          const token = requireToken(await getAccessTokenSilently());
          return {
            headers: {
              ...(headers ?? {}),
              Authorization: `Bearer ${token}`,
              "Request-ID": reqId,
              ...(!skipAccountUri && urlNameRef.current !== null ? { "Account-Uri": urlNameRef.current } : {}),
            },
          };
        } catch (err) {
          if (isSessionExpiredError(err)) {
            reportSessionExpiredRef.current();
            return { headers: { ...(headers ?? {}), "Request-ID": reqId } };
          }
          // Non-login token failure: don't send a tokenless request (backend → UNAUTHORIZED); abort
          // into errorLink's connectivity handling.
          const tokenError = new Error("Authentication token unavailable");
          tokenError.name = "TokenUnavailableError";
          throw tokenError;
        }
      }
    );

    // Forward link — validates Auth0-specific error codes in successful responses.
    //
    // Handles error codes in Axios responses ({ data: { errors: [...] } }), which is non-standard
    // GraphQL. Those are handled by errorLink.
    // eslint-disable-next-line react-hooks/refs -- refs are accessed when the callback fires (outside render), not when the closure is created here
    const forwardLink = new ApolloLink((operation, forward) =>
      forward(operation).pipe(
        map((response) => {
          if (response.data !== null && response.data !== undefined) {
            validateAuth0ApiErrors(
              { data: response.data } as Parameters<typeof validateAuth0ApiErrors>[0],
              navigateRef.current
            );
          }
          return response;
        })
      )
    );

    // Retry transient network failures (e.g. `Failed to fetch`). errorLink sits above this, so
    // connectivity-lost only fires once retries exhaust.
    const retryLink = new RetryLink({
      delay: { initial: 300, max: 3_000, jitter: true },
      attempts: {
        max: 3,
        retryIf: (error): boolean => {
          const statusCode =
            typeof error === "object" && "statusCode" in error && typeof error.statusCode === "number"
              ? error.statusCode
              : 0;
          // Skip 4xx (won't succeed on retry); retry network failures and 5xx.
          return !(statusCode >= 400 && statusCode < 500);
        },
      },
    });

    const httpLink = new HttpLink({ fetch: sbFetch, uri: "/api/graphql" });

    const dataIdFromObject: NonNullable<InMemoryCacheConfig["dataIdFromObject"]> = (object) => {
      if (object.__typename === "UserAccounts" && object.roles !== null && object.roles !== undefined) {
        const id = typeof object.id === "string" || typeof object.id === "number" ? String(object.id) : "";
        // roles is unknown; join if array of strings, else convert to string safely
        const rolesVal: unknown = object.roles;
        const roles = Array.isArray(rolesVal) ? (rolesVal as string[]).join(",") : String(rolesVal);
        return `${object.__typename}${id}${roles}`;
      }
      if (object.id === null) {
        console.error("Null ID for", object.__typename);
        return defaultDataIdFromObject(object);
      }
      return defaultDataIdFromObject(object);
    };

    type FlowsKeyArgsFn = NonNullable<FieldPolicy["keyArgs"]>;
    const flowsKeyArgs: FlowsKeyArgsFn = (args) =>
      args !== null && "backfill_id" in args ? (args.backfill_id as string) : false;

    const cache = new InMemoryCache({
      dataIdFromObject,
      typePolicies: {
        Query: {
          fields: {
            drafts: relayStylePagination(),
            history: relayStylePagination(),
            downloads: relayStylePagination(),
            events: relayStylePagination(),
            uploads: relayStylePagination(),
            flows: relayStylePagination(flowsKeyArgs),
            backfills: relayStylePagination(),
            accounts: relayStylePagination(),
            accountsV2: relayStylePagination(),
            users: relayStylePagination(),
            credentials: relayStylePagination(),
          },
        },
      },
    });

    return new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, forwardLink, retryLink, httpLink]),
      cache,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- client intentionally created once; refs carry dynamic values
  }, []);

  // Clear the Apollo cache whenever the user switches to a different account.
  // Without this, InMemoryCache serves stale data from the previous account until a full page reload.
  useEffect(() => {
    if (prevUrlNameRef.current === undefined) {
      // First render — just record the initial urlName, no cache action needed.
      prevUrlNameRef.current = urlName;
      return;
    }
    if (prevUrlNameRef.current !== urlName) {
      const previousUrlName = prevUrlNameRef.current;
      // Always advance the ref so the next render has the correct baseline.
      prevUrlNameRef.current = urlName;
      // Only clear the cache when switching between two real accounts (both non-null).
      if (previousUrlName !== null && urlName !== null) {
        const clearCacheOnAccountSwitch = async (): Promise<void> => {
          try {
            await client.cache.reset();
          } catch (err) {
            Sentry.captureException(err, {
              tags: { previousUrlName: previousUrlName, nextUrlName: urlName },
            });
          }
        };
        void clearCacheOnAccountSwitch();
      }
    }
  }, [urlName, client]);

  // On session expiry, drop the data that outlives the unmounted DOM: the Apollo cache plus the
  // global store / localStorage state (via clearClientData), so nothing sensitive lingers in JS.
  useEffect(() => {
    if (!sessionExpired) {
      return;
    }
    const purge = async (): Promise<void> => {
      try {
        await client.clearStore();
      } catch (err) {
        Sentry.captureException(err, { tags: { context: "session-expired-clear-store" } });
      }
      clearClientData();
    };
    void purge();
  }, [sessionExpired, client]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
